import React from "react";
import {FormFields} from "../../../../models/forms/form-field.interface";
import {
    IBookingSearchByContactRequest,
    IBookingSearchPagination
} from "../../../airline-webapi/requests/booking-search.requests";
import {IBookingSearchResultRecord} from "../../../airline-webapi/responses/booking-search.response";
import {BookingSearchStrategyType} from "../booking-search.strategy.interface";
import {NullableString} from "../../../../types/nullable-types";
import {SearchByContactComponent} from "./search-by-contact.component";
import {BookingSearchStrategyBase} from "../booking-search-strategy-base";
import {homeOutline} from "ionicons/icons";

interface ISearchByContactFields {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    recordLocator: string;
    emailAddress: string;
}

interface ISearchByContactPersistedFields {
    firstName: NullableString;
    lastName: NullableString;
    phoneNumber: NullableString;
    recordLocator: NullableString;
    emailAddress: NullableString;
}

export class SearchByContactStrategy extends BookingSearchStrategyBase<ISearchByContactFields, ISearchByContactPersistedFields> {

    get type(): BookingSearchStrategyType {
        return BookingSearchStrategyType.ByContact;
    }

    get name(): string {
        return this.services.language.translate("By contact");
    }

    get resultsDescription(): string {
        return this.services.language.translate("Results by contact");
    }

    protected _getIonIconName(): NullableString {
        return homeOutline;
    }

    protected _createFilterFields(persistedFields: ISearchByContactPersistedFields): FormFields<ISearchByContactFields> {
        return {
            firstName: this._createField<string>({
                fieldName: () => this.services.language.translate("First name"),
                defaultValue: persistedFields?.firstName ?? null,
                isRequired: false
            }),
            lastName: this._createField<string>({
                fieldName: () => this.services.language.translate("Last name"),
                defaultValue: persistedFields?.lastName ?? null,
                isRequired: true
            }),
            phoneNumber: this._createField<string>({
                fieldName: () => this.services.language.translate("Phone number"),
                defaultValue: persistedFields?.phoneNumber ?? null,
                isRequired: false
            }),
            recordLocator: this._createField<string>({
                fieldName: () => this.services.language.translate("Record locator"),
                defaultValue: persistedFields?.recordLocator ?? null,
                isRequired: false
            }),
            emailAddress: this._createEmailField({
                fieldName: () => this.services.language.translate("Email address"),
                defaultValue: persistedFields?.recordLocator ?? null,
                isRequired: false,
            })
        }
    }

    renderSearchFields(): React.ReactElement {
        return (
            <SearchByContactComponent strategy={this} />
        );
    }

    async _searchBookings(sessionToken: string, pagination: IBookingSearchPagination): Promise<IBookingSearchResultRecord[]> {
        const request: IBookingSearchByContactRequest = {
            dotRezToken: sessionToken,
            pagination: pagination,
            ...(this.fields.firstName.value && { firstName: this.fields.firstName.value }),
            ...(this.fields.lastName.value && { lastName: this.fields.lastName.value }),
            ...(this.fields.phoneNumber.value && { phoneNumber: this.fields.phoneNumber.value }),
            ...(this.fields.recordLocator.value && { recordLocator: this.fields.recordLocator.value }),
            ...(this.fields.emailAddress.value && { emailAddress: this.fields.emailAddress.value })
        };

        const response = await this.services.airlineWebapi.agencyBookingSearchByContact(request);
        return response.results;
    }

    protected _collectFieldsValuesForStorage(): ISearchByContactPersistedFields {
        return {
            firstName: this.fields.firstName.value,
            lastName: this.fields.lastName.value,
            phoneNumber: this.fields.phoneNumber.value,
            recordLocator: this.fields.recordLocator.value,
            emailAddress: this.fields.emailAddress.value,

        };
    }
}

